import React, { useState } from 'react';
import {
  Hourglass, IdentificationBadge, TrendUp, Wrench, Gear,
  Sparkle, Target, Sunglasses, Trophy, CarProfile,
} from '@phosphor-icons/react';
import { api } from '../api';
import PasswordField from '../components/PasswordField';

const ROLES = [
  { id: 'Sales',       Icon: IdentificationBadge, label: 'Sales' },
  { id: 'SalesMgr',    Icon: TrendUp,             label: 'Sales Mgr' },
  { id: 'WorkshopMgr', Icon: Wrench,              label: 'W/S Mgr' },
  { id: 'Mechanic',    Icon: Gear,                label: 'Mechanic' },
  { id: 'Detail',      Icon: Sparkle,             label: 'Detail' },
  { id: 'DetailMgr',   Icon: Target,              label: 'Detail Mgr' },
  { id: 'Tint',        Icon: Sunglasses,          label: 'Tint' },
  { id: 'TintMgr',     Icon: Trophy,              label: 'Tint Mgr' },
  { id: 'DeliveryMgr', Icon: CarProfile,          label: 'Delivery Mgr' },
];

export default function Signup({ onSignedUp, onSwitchToLogin }) {
  const [name, setName]         = useState('');
  const [role, setRole]         = useState('Sales');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!name.trim() || name.trim().length < 2) return setError('Name must be at least 2 characters');
    if (!password || password.length < 4)        return setError('Password must be at least 4 characters');
    if (password !== confirm)                     return setError('Passwords do not match');

    setBusy(true);
    try {
      const resp = await api.signup({ name: name.trim(), role, password });
      if (resp.firstUser && resp.token) {
        // First Manager: log in automatically
        onSignedUp({ user: resp.user, token: resp.token, firstUser: true });
      } else {
        setSuccess({
          name: resp.user.name,
          role: resp.user.role,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Pending-approval success screen
  if (success) {
    return (
      <div className="screen-login">
        <div className="ll" style={{ justifyContent: 'center' }}>
          <div className="ll-logo">
            <div className="ll-logo-sq"><img src="/vehicle-flow-logo-256.png" alt="VF" /></div>
            <div>
              <div className="ll-logo-name">Vehicle Flow</div>
              <div className="ll-logo-sub">Melville Motors Group</div>
            </div>
          </div>

          <div style={{
            background: 'rgba(47,168,224,.12)',
            border: '1px solid rgba(47,168,224,.32)',
            borderRadius: 12,
            padding: 22,
            marginBottom: 20,
          }}>
            <div style={{ marginBottom: 10 }}><Hourglass size={32} weight="duotone" color="var(--accent-bright)" /></div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
              Account Pending Approval
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', lineHeight: 1.5 }}>
              Hi <strong style={{ color: 'var(--accent)' }}>{success.name}</strong> ({success.role}).
              Your account was created and is now waiting for the Manager to approve it.
              You'll be able to sign in once approved.
            </div>
          </div>

          <button type="button" className="btn-in" onClick={onSwitchToLogin}>
            Back to Sign In →
          </button>
        </div>

        <div className="lr">
          <img src="/login-banner.jpg" alt="Vehicle Flow — Every car. Every team. One screen." className="lr-banner" />
        </div>
      </div>
    );
  }

  return (
    <div className="screen-login">
      <div className="ll">
        <div className="ll-logo">
          <div className="ll-logo-sq"><img src="/vehicle-flow-logo-256.png" alt="VF" /></div>
          <div>
            <div className="ll-logo-name">Vehicle Flow</div>
            <div className="ll-logo-sub">Melville Motors Group</div>
          </div>
        </div>
        <div className="ll-title">Create account</div>
        <div className="ll-sub">Sign up — first user becomes Manager. Others wait for approval.</div>

        <form onSubmit={submit}>
          <label className="lfl">Your Name</label>
          <input
            className="lfi"
            type="text"
            placeholder="First name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            autoComplete="username"
          />

          <label className="lfl">Department</label>
          <div className="rg" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {ROLES.map((r) => (
              <div
                key={r.id}
                className={`rb ${role === r.id ? 'sel' : ''}`}
                onClick={() => setRole(r.id)}
              >
                <div className="ri"><r.Icon size={19} weight={role === r.id ? 'fill' : 'duotone'} /></div>
                <div className="rn">{r.label}</div>
              </div>
            ))}
          </div>

          <label className="lfl">Password</label>
          <PasswordField
            placeholder="At least 4 characters"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
          />

          <label className="lfl">Confirm Password</label>
          <PasswordField
            placeholder="Repeat password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
          />

          {error && (
            <div style={{
              background: 'rgba(187,22,43,.15)',
              border: '1px solid rgba(187,22,43,.4)',
              color: '#ff8a98',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 14,
            }}>{error}</div>
          )}

          <button type="submit" className="btn-in" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account →'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--sidebar-muted)' }}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            style={{
              background: 'transparent', border: 'none', color: 'var(--accent)',
              cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', fontSize: 12,
              textDecoration: 'underline', padding: 0,
            }}
          >Sign in</button>
        </div>

        <div className="ll-foot">Powered by <span>Acts Car Detailing</span></div>
      </div>

      <div className="lr">
        <div className="lr-circles">
          <div className="lr-circle"></div>
          <div className="lr-circle"></div>
          <div className="lr-circle"></div>
        </div>
        <div className="lr-content">
          <img src="/vehicle-flow-logo-256.png" alt="Vehicle Flow" className="lr-logo-img" />
          <div className="lr-sub">Every car. Every team. One screen.</div>
          <div className="lr-brands">
            <span className="lr-brand k">KIA</span>
            <span className="lr-brand s">SUZUKI</span>
            <span className="lr-brand g">GWM</span>
          </div>
        </div>
      </div>
    </div>
  );
}
